/**
 * ThemeState - State management for theme system
 * Copyright 2024 Delanoe Pirard / Aedelon - Apache 2.0
 */

import { utils } from '../../core/utils.js';
import { eventBus } from '../../core/eventBus.js';

export const THEMES = {
  LIGHT: 'light',
  DARK: 'dark',
  AUTO: 'auto'
};

export class ThemeState {
  constructor() {
    this.body = document.body;
    this.currentMode = this.getSavedMode() || THEMES.AUTO;
    this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    this.autoModeInterval = null;
  }

  /**
   * Get saved theme preference from localStorage
   */
  getSavedMode() {
    return utils.storage.get('theme-preference');
  }

  /**
   * Save theme preference to localStorage
   */
  saveMode(mode) {
    utils.storage.set('theme-preference', mode);
  }

  /**
   * Get the effective theme based on current mode
   * For AUTO mode, checks system preference then time-based fallback
   */
  getEffectiveTheme() {
    if (this.currentMode === THEMES.AUTO) {
      // Check system preference first
      if (this.mediaQuery.matches) {
        return THEMES.DARK;
      }

      // Fallback to time-based switching (dark: 19h-7h)
      const hour = new Date().getHours();
      if (hour >= 19 || hour < 7) {
        return THEMES.DARK;
      }

      return THEMES.LIGHT;
    }

    return this.currentMode;
  }

  /**
   * Set the current mode and save it
   */
  setMode(mode) {
    if (Object.values(THEMES).includes(mode)) {
      this.currentMode = mode;
      this.saveMode(mode);
      return true;
    }
    return false;
  }

  /**
   * Cycle to next theme mode: light -> dark -> auto -> light
   */
  cycleMode() {
    const modes = [THEMES.LIGHT, THEMES.DARK, THEMES.AUTO];
    const currentIndex = modes.indexOf(this.currentMode);
    const nextIndex = (currentIndex + 1) % modes.length;
    this.currentMode = modes[nextIndex];
    this.saveMode(this.currentMode);
    return this.currentMode;
  }

  /**
   * Apply theme to DOM
   */
  applyTheme(theme) {
    this.body.setAttribute('data-theme', theme);
    this.updateMetaThemeColor(theme === THEMES.DARK);

    eventBus.emit('theme:changed', {
      mode: this.currentMode,
      theme: theme,
      isDark: theme === THEMES.DARK
    });
  }

  /**
   * Update meta theme-color for browser chrome
   */
  updateMetaThemeColor(isDark) {
    let metaTheme = document.querySelector('meta[name="theme-color"]');
    if (!metaTheme) {
      metaTheme = document.createElement('meta');
      metaTheme.name = 'theme-color';
      document.head.appendChild(metaTheme);
    }
    metaTheme.content = isDark ? '#111827' : '#4338CA';
  }

  /**
   * Setup listeners for AUTO mode
   */
  setupAutoMode(onThemeChange) {
    // Listen for system theme changes
    this.mediaQuery.addEventListener('change', () => {
      if (this.currentMode === THEMES.AUTO) {
        onThemeChange();
      }
    });

    // Check time-based switching every minute (for AUTO mode fallback)
    this.autoModeInterval = setInterval(() => {
      if (this.currentMode === THEMES.AUTO) {
        const currentTheme = this.getEffectiveTheme();
        const appliedTheme = this.body.getAttribute('data-theme') || THEMES.LIGHT;

        if (currentTheme !== appliedTheme) {
          onThemeChange();
        }
      }
    }, 60000);
  }

  /**
   * Track theme change in analytics
   */
  trackChange() {
    if (typeof gtag !== 'undefined') {
      gtag('event', 'theme_change', {
        theme_mode: this.currentMode,
        effective_theme: this.getEffectiveTheme()
      });
    }
  }

  /**
   * Get current theme info (public API)
   */
  getThemeInfo() {
    return {
      mode: this.currentMode,
      theme: this.getEffectiveTheme(),
      isDark: this.body.getAttribute('data-theme') === THEMES.DARK
    };
  }

  /**
   * Get system preference
   */
  getSystemPreference() {
    return this.mediaQuery.matches ? THEMES.DARK : THEMES.LIGHT;
  }

  /**
   * Cleanup
   */
  destroy() {
    if (this.autoModeInterval) {
      clearInterval(this.autoModeInterval);
    }
  }
}
